import { Eye, Globe, MoreHorizontal, Power, PowerOff, Shield } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaDropdownMenu } from "@/shared/components/overlays/ColocaDropdownMenu";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { ApiKeyEntity } from "../../services/apiKeysService";

export interface AccessApiKeyActionsProps {
  apiKey: ApiKeyEntity;
  /** Null when the roles drawer cannot be offered (catalog 403'd, or this key's roles failed to load). */
  onOpenRoles: (() => void) | undefined;
  canManageRoles: boolean;
  canUpdate: boolean;
  onEditIps: (key: ApiKeyEntity) => void;
  onDisable: (key: ApiKeyEntity) => void;
  onEnable: (key: ApiKeyEntity) => void;
  /** Stretch the primary button to fill the row — the card layout, where it is the full card width. */
  spread?: boolean;
}

/**
 * A key's row actions, shared by the table and the cards.
 *
 * The lifecycle menu stays behind its own trigger even on a phone, and deliberately: "Disable" is the one
 * destructive action on this surface, and putting it adjacent to the primary button — which on a card is a
 * full-width target — is how a thumb reaches it by accident. It also does not depend on the roles catalog, so
 * it survives when the roles button cannot be offered.
 */
export function AccessApiKeyActions({
  apiKey,
  onOpenRoles,
  canManageRoles,
  canUpdate,
  onEditIps,
  onDisable,
  onEnable,
  spread = false,
}: AccessApiKeyActionsProps) {
  const { t } = useTranslations("settings");

  return (
    <div className={cn("flex items-center gap-1.5", spread ? "w-full" : "justify-end")}>
      {onOpenRoles && (
        <ColocaButton
          variant="default"
          size="sm"
          icon={canManageRoles ? Shield : Eye}
          onClick={onOpenRoles}
          className={cn(spread && "flex-1")}
        >
          {canManageRoles ? t("access_action_roles") : t("access_action_view")}
        </ColocaButton>
      )}
      {canUpdate && (
        <ColocaDropdownMenu
          align="end"
          trigger={
            <button
              type="button"
              aria-label={t("access_apikeys_more_actions")}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <MoreHorizontal size={15} />
            </button>
          }
          items={[
            {
              label: t("api_keys_edit_ips_button"),
              icon: Globe,
              onClick: () => onEditIps(apiKey),
            },
            apiKey.is_active
              ? {
                  label: t("api_keys_disable_action"),
                  icon: PowerOff,
                  variant: "destructive",
                  onClick: () => onDisable(apiKey),
                }
              : {
                  label: t("api_keys_enable_action"),
                  icon: Power,
                  onClick: () => onEnable(apiKey),
                },
          ]}
        />
      )}
    </div>
  );
}
